import { useCallback, useEffect, useState } from "react";
import { Link } from "wouter";
import Sidebar from "@/components/Sidebar";
import { adminResetUserPassword, apiJson } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type UserRow = {
  id: number;
  login: string;
  role: string;
  is_active: number;
  is_registered: number;
  display_name: string | null;
  created_at: string;
  allowlist_id: number | null;
};

type AllowRow = {
  id: number;
  identifier: string;
  intended_role: string;
  can_register: number;
  registered_user_id: number | null;
  display_name: string | null;
  created_at: string;
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [allowlist, setAllowlist] = useState<AllowRow[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"editor" | "admin" | "viewer">("editor");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [resetting, setResetting] = useState<number | null>(null);
  const [resetResult, setResetResult] = useState<
    | { login: string; temporaryPassword: string; copied: boolean }
    | null
  >(null);

  const load = useCallback(async () => {
    const data = await apiJson<{ users: UserRow[]; allowlist: AllowRow[] }>("/api/admin/users");
    setUsers(data.users);
    setAllowlist(data.allowlist);
  }, []);

  useEffect(() => {
    load().catch((e) => setError(String(e)));
  }, [load]);

  async function invite(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setMsg(null);
    try {
      await apiJson("/api/admin/users/invite", {
        method: "POST",
        body: JSON.stringify({ email: inviteEmail, role: inviteRole }),
      });
      setInviteEmail("");
      setMsg("Приглашение добавлено");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    }
  }

  async function toggleActive(u: UserRow) {
    if (u.login === "guest") return;
    setError(null);
    try {
      await apiJson(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: !u.is_active }),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    }
  }

  async function resetPassword(u: UserRow) {
    if (u.login === "guest") return;
    if (
      !confirm(
        `Сбросить пароль пользователя ${u.login}?\nБудет сгенерирован одноразовый временный пароль. Пользователь обязан будет сменить его при следующем входе.`,
      )
    ) {
      return;
    }
    setError(null);
    setMsg(null);
    setResetting(u.id);
    try {
      const { login, temporaryPassword } = await adminResetUserPassword(u.id);
      setResetResult({ login, temporaryPassword, copied: false });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось сбросить пароль");
    } finally {
      setResetting(null);
    }
  }

  async function copyTempPassword() {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.temporaryPassword);
      setResetResult({ ...resetResult, copied: true });
    } catch {
      setResetResult({ ...resetResult, copied: false });
    }
  }

  async function removeUser(u: UserRow) {
    if (u.login === "guest") return;
    if (!confirm(`Удалить пользователя ${u.login}?`)) return;
    setError(null);
    try {
      await apiJson(`/api/admin/users/${u.id}`, { method: "DELETE", body: JSON.stringify({}) });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка");
    }
  }

  async function saveUserDisplayName(u: UserRow, value: string): Promise<void> {
    const nextName = value.trim() ? value.trim() : null;
    if ((u.display_name ?? null) === nextName) return;
    setError(null);
    setMsg(null);
    const resp = await apiJson<{ user: UserRow }>(`/api/admin/users/${u.id}`, {
      method: "PATCH",
      body: JSON.stringify({ display_name: nextName }),
    });
    setUsers((prev) =>
      prev.map((row) =>
        row.id === u.id ? { ...row, display_name: resp.user?.display_name ?? nextName } : row,
      ),
    );
    setMsg(`ФИО сохранено для ${u.login}`);
  }

  async function changeUserRole(u: UserRow, role: string): Promise<void> {
    if (u.login === "guest" || role === u.role) return;
    if (!window.confirm(`Изменить роль пользователя ${u.login}: ${u.role} → ${role}?`)) return;
    setError(null);
    setMsg(null);
    try {
      await apiJson(`/api/admin/users/${u.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role }),
      });
      setUsers((prev) => prev.map((row) => (row.id === u.id ? { ...row, role } : row)));
      setMsg(`Роль обновлена: ${u.login} → ${role}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось изменить роль");
    }
  }

  async function saveAllowlistDisplayName(a: AllowRow, value: string): Promise<void> {
    const nextName = value.trim() ? value.trim() : null;
    if ((a.display_name ?? null) === nextName) return;
    setError(null);
    setMsg(null);
    const resp = await apiJson<{ allowlist: AllowRow }>(`/api/admin/users/allowlist/${a.id}`, {
      method: "PATCH",
      body: JSON.stringify({ display_name: nextName }),
    });
    setAllowlist((prev) =>
      prev.map((row) =>
        row.id === a.id ? { ...row, display_name: resp.allowlist?.display_name ?? nextName } : row,
      ),
    );
    setMsg(`ФИО сохранено для ${a.identifier}`);
  }

  // «Приглашённые» = только те, для кого ещё НЕТ учётной записи (ни по
  // registered_user_id, ни по совпадению логина/почты). Если запись уже есть —
  // она показывается в «Учётные записи» выше, и дублировать её здесь не нужно.
  // Проверяем оба признака: в части данных registered_user_id не проставлен,
  // хотя пользователь фактически зарегистрирован.
  const userIds = new Set(users.map((u) => u.id));
  const userLogins = new Set(users.map((u) => u.login.trim().toLowerCase()));
  const pendingInvites = allowlist
    .filter(
      (a) =>
        !(a.registered_user_id != null && userIds.has(a.registered_user_id)) &&
        !userLogins.has(a.identifier.trim().toLowerCase()),
    )
    .sort((x, y) => x.identifier.localeCompare(y.identifier, "ru"));

  return (
    <div className="min-h-screen flex" style={{ background: "var(--background)" }}>
      <Sidebar />
      <main className="max-w-6xl flex-1 p-8 transition-[margin-left] duration-200 ease-out [margin-left:var(--app-sidebar-width)]">
        <div className="mb-6 flex items-center justify-between gap-4">
          <h1 className="text-2xl font-bold">Пользователи</h1>
          <Button variant="outline" asChild>
            <Link href="/">На главную</Link>
          </Button>
        </div>

        {error ? <p className="text-red-600 text-sm mb-4">{error}</p> : null}
        {msg ? <p className="text-green-700 text-sm mb-4">{msg}</p> : null}

        <CardSection title="Пригласить (allowlist)">
          <form onSubmit={invite} className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                required
                className="w-64"
              />
            </div>
            <div className="space-y-2">
              <Label>Роль</Label>
              <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as "editor" | "admin" | "viewer")}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">editor</SelectItem>
                  <SelectItem value="admin">admin</SelectItem>
                  <SelectItem value="viewer">viewer (просмотр)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit">Добавить в список</Button>
          </form>
          <p className="mt-3 text-xs text-muted-foreground">
            После добавления в список задайте ФИО — оно будет подтягиваться в карточки на канбане
            и в задачи на Ганте вместо почты.
          </p>
        </CardSection>

        <CardSection title="Учётные записи" className="mt-8">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Логин</TableHead>
                <TableHead className="w-[260px]">ФИО (отображаемое имя)</TableHead>
                <TableHead>Роль</TableHead>
                <TableHead>Активен</TableHead>
                <TableHead>Зарегистрирован</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell>{u.id}</TableCell>
                  <TableCell>{u.login}</TableCell>
                  <TableCell>
                    <DisplayNameCell
                      initialValue={u.display_name ?? ""}
                      placeholder="Фамилия Имя Отчество"
                      disabled={u.login === "guest"}
                      onSave={(v) => saveUserDisplayName(u, v)}
                    />
                  </TableCell>
                  <TableCell>
                    {u.login === "guest" ? (
                      <span className="text-muted-foreground">{u.role}</span>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => void changeUserRole(u, v)}>
                        <SelectTrigger className="h-8 w-[130px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">admin</SelectItem>
                          <SelectItem value="editor">editor</SelectItem>
                          <SelectItem value="viewer">viewer</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </TableCell>
                  <TableCell>{u.is_active ? "да" : "нет"}</TableCell>
                  <TableCell>{u.is_registered ? "да" : "нет"}</TableCell>
                  <TableCell className="text-right space-x-2">
                    {u.login !== "guest" ? (
                      <>
                        <Button type="button" variant="outline" size="sm" onClick={() => toggleActive(u)}>
                          {u.is_active ? "Деактивировать" : "Активировать"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => resetPassword(u)}
                          disabled={resetting === u.id || !u.is_registered}
                          title={
                            !u.is_registered
                              ? "Пользователь ещё не зарегистрировался — сбрасывать нечего"
                              : undefined
                          }
                        >
                          {resetting === u.id ? "Сброс…" : "Сбросить пароль"}
                        </Button>
                        <Button type="button" variant="destructive" size="sm" onClick={() => removeUser(u)}>
                          Удалить
                        </Button>
                      </>
                    ) : (
                      <span className="text-muted-foreground text-sm">защищён</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardSection>

        <CardSection
          title={`Список приглашённых (ещё не зарегистрированы)${
            pendingInvites.length ? ` — ${pendingInvites.length}` : ""
          }`}
          className="mt-8"
        >
          {pendingInvites.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Нет ожидающих приглашений — все приглашённые уже зарегистрированы. Их ФИО
              редактируется в таблице «Учётные записи» выше.
            </p>
          ) : (
            <>
              <p className="mb-4 text-xs text-muted-foreground">
                Здесь только те, кого пригласили, но кто ещё ни разу не входил. Заданное ФИО
                подставится автоматически при первой регистрации.
              </p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Идентификатор</TableHead>
                    <TableHead className="w-[260px]">ФИО (отображаемое имя)</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>Приглашён</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pendingInvites.map((a) => (
                    <TableRow key={a.id}>
                      <TableCell>{a.identifier}</TableCell>
                      <TableCell>
                        <DisplayNameCell
                          initialValue={a.display_name ?? ""}
                          placeholder="Фамилия Имя Отчество"
                          onSave={(v) => saveAllowlistDisplayName(a, v)}
                        />
                      </TableCell>
                      <TableCell>{a.intended_role}</TableCell>
                      <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                        {formatInviteDate(a.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardSection>
      </main>

      <Dialog
        open={!!resetResult}
        onOpenChange={(open) => {
          if (!open) setResetResult(null);
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Временный пароль создан</DialogTitle>
            <DialogDescription>
              Передайте этот пароль пользователю
              {resetResult ? ` «${resetResult.login}» ` : " "}
              по доверенному каналу. При следующем входе система попросит
              придумать новый пароль — старый пользователь больше использовать не сможет.
            </DialogDescription>
          </DialogHeader>
          {resetResult ? (
            <div className="space-y-3">
              <div className="rounded-md border border-amber-400/60 bg-amber-50 dark:bg-amber-900/20 p-3">
                <p className="text-xs text-muted-foreground mb-1">
                  Пароль показывается один раз. После закрытия окна восстановить его не получится —
                  только сбросить заново.
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 select-all rounded bg-background px-2 py-1 font-mono text-sm">
                    {resetResult.temporaryPassword}
                  </code>
                  <Button
                    type="button"
                    size="sm"
                    variant={resetResult.copied ? "outline" : "default"}
                    onClick={() => void copyTempPassword()}
                  >
                    {resetResult.copied ? "Скопировано" : "Скопировать"}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Проекты, задачи и история пользователя остаются в сохранности — он увидит
                свой личный кабинет, как только сменит пароль.
              </p>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" onClick={() => setResetResult(null)}>
              Готово
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * Инлайн-поле для ФИО с явной кнопкой «Сохранить» и состояниями:
 * «Не сохранено» / «Сохраняю…» / «Сохранено» / «Ошибка».
 * Сохраняет также по Enter. Esc возвращает исходное значение.
 */
function DisplayNameCell({
  initialValue,
  placeholder,
  disabled,
  disabledHint,
  onSave,
}: {
  initialValue: string;
  placeholder?: string;
  disabled?: boolean;
  disabledHint?: string;
  onSave: (value: string) => Promise<void> | void;
}) {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  const trimmed = value.trim();
  const baseline = (initialValue ?? "").trim();
  const isDirty = trimmed !== baseline;

  const commit = async () => {
    if (!isDirty || saving || disabled) return;
    setSaving(true);
    setErr(null);
    try {
      await onSave(value);
      setJustSaved(true);
      window.setTimeout(() => setJustSaved(false), 1800);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Не удалось сохранить");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        <Input
          type="text"
          value={value}
          placeholder={placeholder}
          disabled={disabled || saving}
          aria-invalid={Boolean(err)}
          onChange={(e) => {
            setValue(e.target.value);
            if (err) setErr(null);
            if (justSaved) setJustSaved(false);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void commit();
            } else if (e.key === "Escape") {
              setValue(initialValue);
              setErr(null);
            }
          }}
          className={`h-8 text-sm ${isDirty ? "border-amber-400 focus-visible:ring-amber-300" : ""}`}
        />
        <Button
          type="button"
          size="sm"
          variant={isDirty ? "default" : "outline"}
          disabled={disabled || saving || !isDirty}
          onClick={() => void commit()}
          className="h-8 px-3 shrink-0"
        >
          {saving ? "Сохраняю…" : "Сохранить"}
        </Button>
      </div>
      {err ? (
        <span className="text-xs text-red-600">{err}</span>
      ) : justSaved ? (
        <span className="text-xs text-green-700">Сохранено</span>
      ) : isDirty ? (
        <span className="text-xs text-amber-600">Не сохранено — нажмите «Сохранить» или Enter</span>
      ) : disabled && disabledHint ? (
        <span className="text-xs text-muted-foreground">{disabledHint}</span>
      ) : null}
    </div>
  );
}

/** Дата приглашения в формате дд.мм.гггг; «—» если значение не распознано. */
function formatInviteDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(d);
}

function CardSection({
  title,
  children,
  className = "",
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <section className={`rounded-lg border border-[var(--sidebar-border)] p-4 ${className}`}>
      <h2 className="text-lg font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}
